import React, { useState } from 'react';
import TextArea from 'components/atoms/textarea';
import DraggableModal from 'components/organisms/draggableModal';
import { showResult } from 'infrastructure/hooks/utils';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import DigitalEngagementSelectBox from 'components/molecules/digital-engagement-select-box';
import { useTracker } from 'infrastructure/hooks/shared/useTracker';

import s from './styles.module.scss';
import { ContactTypeSelect } from './contactType';

import type { DigitalEngagementType } from 'infrastructure/data-sources/digital-engagement';
import type { ContactType } from './contactType';
import type { ISendSmsDataProps } from 'infrastructure/hooks/control-panel/api';

export interface SmsModalProps {
  isOpen: boolean;
  toggle: () => void;
  patientGuid: string;
  loading: boolean;
  submit: (
    data: ISendSmsDataProps,
  ) => Promise<{ error: null | 'close alert' | (string & {}) }>;
  isRpmPanel: boolean;
  withTracker?: boolean;
  isAdminPanel?: boolean;
}

const SmsModal: React.FC<SmsModalProps> = ({
  isOpen,
  toggle,
  patientGuid,
  loading,
  submit,
  isRpmPanel,
  withTracker: _withTracker = false,
  isAdminPanel = false,
}) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState<ContactType>('primary');
  const [digitalEngagement, setDigitalEngagement] =
    useState<DigitalEngagementType | null>();
  const [isDEEmpty, setIsDEEmpty] = useState(false);

  const { createTrackerLoading } = useTracker();

  const handleSubmit = async () => {
    if (digitalEngagement === undefined) {
      setIsDEEmpty(true);
      return;
    }

    if (isDEEmpty) {
      setIsDEEmpty(false);
    }

    const postData: ISendSmsDataProps = {
      patientGuid,
      topic: '',
      type: 'sms',
      message,
      isRpmPanel,
      isPrimaryNumber: contact === 'primary',
      digitalEngagement: digitalEngagement ?? undefined,
      actionFrom: isAdminPanel ? 'admin-panel' : undefined,
    };

    const { error } = await submit(postData);
    if (error === 'close alert') {
      return;
    }

    if (!error) {
      showResult();
      toggle();
    }
  };

  return (
    <>
      {isOpen && (
        <DraggableModal
          title={t('labels.sendSms')}
          isOpen={isOpen}
          toggle={() => {
            toggle();
          }}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          onSave={handleSubmit}
          isSaveDisabled={!message || createTrackerLoading}
          isSaveLoading={loading}
          saveLabel={t('controls.send')}
        >
          <div className={classNames(s['modal-content'], 'sms-popup-content')}>
            <div>
              <TextArea
                className="sms-textarea"
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder={t('labels.typeOrSelectOption')}
              />
              <ContactTypeSelect
                patientGuid={patientGuid}
                onChange={setContact}
                value={contact}
              />
              <DigitalEngagementSelectBox
                placeholder={t('digitalEngagement.digitalEngagement')}
                id="digital_engagement"
                dataCy="digital-engagement-select-box"
                value={digitalEngagement}
                onChange={(v) => setDigitalEngagement(v)}
                withNull
                defaultValue={undefined}
                invalid={isDEEmpty}
              />
            </div>
          </div>
        </DraggableModal>
      )}
    </>
  );
};

export default SmsModal;
